import {
  BlotterTable,
  Box,
  useBlotterObservables,
  useBlotterTable,
  useBlotterVisibleRowsEnrichment,
  usePersistedBlotterTable,
  useVisibleCellsChanged,
  VStack,
} from '@talos/kyoko';
import type { Column, GridApi } from 'ag-grid-community';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { AssetLeaderboardRow, AssetLeaderboardSortPreset } from './AssetLeaderboard.types';
import { AssetLeaderboardEntity, type AssetLeaderboardPriceEnrichment } from './AssetLeaderboardEntity';
import { ASSET_LEADERBOARD_PRICE_COLUMN_ID, ASSET_LEADERBOARD_SORT_PRESET_COLUMN } from './tokens';
import { useAssetLeaderboardColumns } from './useAssetLeaderboardColumns';
import { useAssetLeaderboardPriceEnrichment } from './useAssetLeaderboardPriceEnrichment';

interface AssetLeaderboardViewProps {
  rows: AssetLeaderboardRow[];
  blotterID: string;
  /** The active toolbar preset, or null when the user is sorting by a column directly. */
  sortPreset: AssetLeaderboardSortPreset | null;
  /** True once the user has explicitly clicked a toolbar preset (see AssetLeaderboardModule). */
  hasUserChangedSortPreset: boolean;
  /** Called when the grid's sort no longer matches the active preset, so the toolbar can deselect it. */
  onSortClearedByColumn: () => void;
}

// Stable references (module scope) so they don't retrigger the enrichment observables on every render.
const getEntityKey = (entity: AssetLeaderboardEntity): string => entity.id;
const getEnrichmentKey = (enrichment: AssetLeaderboardPriceEnrichment): string => enrichment.cmAssetId;
const isPriceColumnRelevant = (column: Column): boolean => column.getColId() === ASSET_LEADERBOARD_PRICE_COLUMN_ID;
const getPriceEnrichmentLookupKey = ({
  item,
  column,
}: {
  item: AssetLeaderboardEntity;
  column: Column;
}): string | undefined => (column.getColId() === ASSET_LEADERBOARD_PRICE_COLUMN_ID ? item.cmAssetId : undefined);

/**
 * Flat, sortable asset list. Grid state (sort/column order/visibility) persists per panel instance
 * via `usePersistedBlotterTable`.
 *
 * Live price uses the PriceGrid-era enrichment pipeline: `useVisibleCellsChanged` tracks the rendered
 * rows + visible columns, `useAssetLeaderboardPriceEnrichment` subscribes the CoinMetrics stream to just
 * those visible price cells, and `useBlotterVisibleRowsEnrichment` merges each tick onto only the
 * affected rows — emitting row-level deltas into `useBlotterTable` (which applies them via
 * `applyTransactionAsync`) rather than rebuilding and re-diffing the whole universe on every tick.
 */
export function AssetLeaderboardView({
  rows,
  blotterID,
  sortPreset,
  hasUserChangedSortPreset,
  onSortClearedByColumn,
}: AssetLeaderboardViewProps) {
  const columns = useAssetLeaderboardColumns();
  const persistedBlotterTable = usePersistedBlotterTable(blotterID);
  // React state, not a ref: the sort-preset effect below only re-runs when this reference changes,
  // which a ref update never triggers.
  const [gridApi, setGridApi] = useState<GridApi | null>(null);

  const entities = useMemo(() => rows.map(row => new AssetLeaderboardEntity(row)), [rows]);
  const { dataObservable, errorObservable } = useBlotterObservables({ data: entities });

  const { visibleCellsChangedObs, gridOptionsOverlay } = useVisibleCellsChanged<AssetLeaderboardEntity>({
    getUniqueKey: getEntityKey,
    isColumnRelevant: isPriceColumnRelevant,
  });

  const enrichmentObservable = useAssetLeaderboardPriceEnrichment(visibleCellsChangedObs);

  const { enrichedDataObservable } = useBlotterVisibleRowsEnrichment<
    AssetLeaderboardEntity,
    AssetLeaderboardPriceEnrichment
  >({
    dataObservable,
    enrichmentObservable,
    visibleCellsChangedObs,
    getUniqueKey: getEntityKey,
    getUniqueEnrichmentKey: getEnrichmentKey,
    getEnrichmentLookupKey: getPriceEnrichmentLookupKey,
  });

  const blotterTable = useBlotterTable<AssetLeaderboardEntity>({
    dataObservable: enrichedDataObservable,
    errorObservable,
    columns,
    rowID: 'id' satisfies keyof AssetLeaderboardEntity,
    persistence: persistedBlotterTable,
    gridOptions: {
      // Viewport/column-change handlers that drive the visible-rows enrichment pipeline.
      ...gridOptionsOverlay,
      // Taller than the grid default so the change columns' sparkline has room beneath the percent.
      rowHeight: 48,
      // Keep off-screen rows unmounted so the live-price subscription tracks exactly what's rendered.
      rowBuffer: 0,
      onGridReady: params => {
        setGridApi(params.api);
      },
    },
  });

  // Jumps the grid to the preset's column + direction — the same effect as clicking that column's
  // header — so the resulting sort flows through the normal onStateUpdated persistence path and is
  // saved like any manual sort. Only forces this when the user actually clicked a preset
  // (hasUserChangedSortPreset): AG Grid restores persisted sort/column state before onGridReady fires,
  // so applying the coded default unconditionally here would silently clobber (and then persist over)
  // a returning user's real sort on every mount, since sortPreset always starts at the default value
  // regardless of what's already on the grid. When the user hasn't touched the toolbar yet, only apply
  // if the grid doesn't already have some sort active (covers the true first-ever load as a safety net,
  // in case the Market Cap column's own `initialSort` didn't apply for some reason).
  useEffect(() => {
    if (!gridApi || sortPreset == null) {
      return;
    }
    if (!hasUserChangedSortPreset) {
      const hasExistingSort = gridApi.getColumnState().some(column => column.sort != null);
      if (hasExistingSort) {
        return;
      }
    }
    const { colId, sort } = ASSET_LEADERBOARD_SORT_PRESET_COLUMN[sortPreset];
    gridApi.applyColumnState({ state: [{ colId, sort }], defaultState: { sort: null } });
  }, [sortPreset, gridApi, hasUserChangedSortPreset]);

  // Deselect the active preset the moment the grid's sort stops matching it — i.e. the user clicked a
  // column header. Applying a preset ourselves (above) also fires `sortChanged`, but that lands on the
  // preset's own (colId, direction) so it matches and is left selected; only a genuine manual sort
  // clears it. Refs keep the listener stable (attached once per grid) while always reading the latest
  // preset + callback.
  const sortPresetRef = useRef(sortPreset);
  sortPresetRef.current = sortPreset;
  const onSortClearedByColumnRef = useRef(onSortClearedByColumn);
  onSortClearedByColumnRef.current = onSortClearedByColumn;
  useEffect(() => {
    if (!gridApi) {
      return;
    }
    const handleSortChanged = () => {
      const activePreset = sortPresetRef.current;
      if (activePreset == null) {
        return;
      }
      const expected = ASSET_LEADERBOARD_SORT_PRESET_COLUMN[activePreset];
      const sortedColumns = gridApi.getColumnState().filter(column => column.sort != null);
      const matchesPreset =
        sortedColumns.length === 1 &&
        sortedColumns[0].colId === expected.colId &&
        sortedColumns[0].sort === expected.sort;
      if (!matchesPreset) {
        onSortClearedByColumnRef.current();
      }
    };
    gridApi.addEventListener('sortChanged', handleSortChanged);
    return () => gridApi.removeEventListener('sortChanged', handleSortChanged);
  }, [gridApi]);

  return (
    <VStack flex="1" w="100%" minHeight={0}>
      <Box flex="1" w="100%" minHeight={0}>
        <BlotterTable {...blotterTable} data-testid="asset-leaderboard-list" />
      </Box>
    </VStack>
  );
}
